import { getPhysicsMouthGateTopY } from "./geometry.js";

/**
 * Wall slab depth as a share of the jar's inner height. Only the inner face is the jar; the rest
 * reaches outward. A hard shake moves a cell tens of px in one step, and Matter pushes a body out
 * through the nearer face — a thin wall let it end past the midline and pop out the far side.
 */
var WALL_SLAB_DEPTH_FRAC = 0.5;

function getWallSlabDepth(geometry) {
  var innerH = Math.max(1, geometry.bodyBottom - geometry.T);
  return Math.max(geometry.W, innerH * WALL_SLAB_DEPTH_FRAC);
}

/** Slab whose inner face runs (x0,y0)→(x1,y1); `side` −1 grows it to the left, +1 to the right. */
function createSideSlab(Bodies, x0, y0, x1, y1, depth, side, wallOptions) {
  var dx = x1 - x0;
  var dy = y1 - y0;
  var len = Math.sqrt(dx * dx + dy * dy);
  var nx = dy / len;
  var ny = -dx / len;
  if (nx * side < 0) {
    nx = -nx;
    ny = -ny;
  }
  var opts = Object.assign({}, wallOptions, { angle: Math.atan2(dy, dx) - Math.PI / 2 });
  return Bodies.rectangle(
    (x0 + x1) / 2 + nx * depth / 2,
    (y0 + y1) / 2 + ny * depth / 2,
    depth,
    len,
    opts
  );
}

/**
 * Floor + one solid slab per side (plus a neck slab when the neck is narrower than the floor).
 * A wall stacked from short pieces has seams: a creature sliding down it could catch a piece's
 * top corner and get kicked upward.
 */
export function createFlaskWalls(Bodies, geometry, wallOptions) {
  var cx = geometry.cx;
  var bodyTop = geometry.bodyTop;
  var bodyBottom = geometry.bodyBottom;
  var neckLeft = geometry.neckLeft;
  var neckRight = geometry.neckRight;
  var floorLeft = geometry.floorLeft != null ? geometry.floorLeft : cx - geometry.floorHalfWidth;
  var floorRight = geometry.floorRight != null ? geometry.floorRight : cx + geometry.floorHalfWidth;
  var floorHalfWidth = geometry.floorHalfWidth;
  var depth = getWallSlabDepth(geometry);
  var gateTopY = getPhysicsMouthGateTopY(geometry);
  var hasNeck = bodyTop - gateTopY > 1;

  var walls = [];

  // Floor
  walls.push(Bodies.rectangle(cx, bodyBottom + depth / 2, floorHalfWidth * 2 + depth * 2, depth, wallOptions));

  var sides = [
    { side: -1, neckX: neckLeft, floorX: floorLeft },
    { side: 1, neckX: neckRight, floorX: floorRight },
  ];
  for (var i = 0; i < sides.length; i++) {
    var s = sides[i];
    var straight = Math.abs(s.neckX - s.floorX) < 0.5;
    if (straight) {
      // Neck and body share one face: one slab from the gate down past the floor top.
      var topY = hasNeck ? gateTopY : bodyTop;
      walls.push(createSideSlab(Bodies, s.neckX, topY, s.floorX, bodyBottom + depth, depth, s.side, wallOptions));
      continue;
    }
    if (hasNeck) {
      walls.push(createSideSlab(Bodies, s.neckX, gateTopY, s.neckX, bodyTop, depth, s.side, wallOptions));
    }
    // Angled body wall: no extension above the shoulder (it would poke a lip into the neck);
    // extended below the floor top so the corner has no gap.
    var ldx = s.floorX - s.neckX;
    var ldy = bodyBottom - bodyTop;
    var llen = Math.sqrt(ldx * ldx + ldy * ldy);
    var ext = depth / llen;
    walls.push(createSideSlab(
      Bodies, s.neckX, bodyTop, s.floorX + ldx * ext, bodyBottom + ldy * ext, depth, s.side, wallOptions
    ));
  }

  return walls;
}

/** Stacked solid slabs at the physics gate — blocks solver ejection through a thin lid. */
export function createMouthSealBodies(Bodies, geometry, wallOptions) {
  var neckLeft = geometry.neckLeft;
  var neckRight = geometry.neckRight;
  var W = geometry.W;
  var cx = (neckLeft + neckRight) / 2;
  var sealWidth = Math.max(12, (neckRight - neckLeft) + W * 2);
  var layerHeight = Math.max(8, Math.min(12, W * 0.45));
  var gateTopY = getPhysicsMouthGateTopY(geometry);
  var seals = [];
  var layers = 3;
  for (var i = 0; i < layers; i++) {
    var topY = gateTopY - i * (layerHeight - 3);
    var sealY = topY + layerHeight / 2;
    seals.push(Bodies.rectangle(cx, sealY, sealWidth, layerHeight, wallOptions));
  }
  return seals;
}

export function createMouthSealBody(Bodies, geometry, wallOptions) {
  var seals = createMouthSealBodies(Bodies, geometry, wallOptions);
  return seals.length ? seals[0] : null;
}